<template>
    <v-container>
        <v-row class="mx-1 mb-5 mt-1 heading-title">
            User Information
        </v-row>

        <v-row class="mx-1 my-5 heading-title">
            Control
        </v-row>
        <v-divider />
        <v-row class="mx-1 mt-4 custom-checkbox">
            <v-checkbox
                v-model="allowed"
                label="Access Allowed"
            />
            <v-checkbox
                v-model="readonly"
                label="Read-Only Access"
            />
            <v-checkbox
                v-model="RECEIVE_COMPANY_EOD_AUDIT"
                label="Receive Center/811 EOD Audits"
            />
            <v-checkbox
                v-model="RECEIVE_COMPANY_EOD_REPORT"
                label="Receive LOGiX EOD Report"
            />
            <v-checkbox
                v-model="RECEIVE_OVERDUE_NOTIFICATION"
                label="Receive Overdue Ticket Notification E-Mails"
            />
        </v-row>
        <v-row class="mx-1 mb-2">
            <v-col class="col col-md-6 p-0">
                <v-select
                    v-model="RECEIVE_OVERDUE_NOTIFICATION_INTERVAL"
                    :items="timeIntervals"
                    outlined
                    dense
                    label="Time interval"
                    item-text="name"
                    item-value="value"
                />
            </v-col>
        </v-row>

        <v-row class="mx-1 my-2 heading-title">
            Basic Details
        </v-row>
        <v-divider />
        <v-row class="mx-1 mt-4">
            <v-col class="col col-md-6 p-0 pt-2">
                <v-text-field
                    v-model="name"
                    outlined
                    dense
                    placeholder="Full Name"
                    label="Full Name"
                    :rules="nameRule"
                />
            </v-col>
            <v-col class="col col-md-6 p-0 pl-5 pt-2">
                <v-text-field
                    v-model="account"
                    outlined
                    dense
                    placeholder="Account"
                    label="Account"
                    :rules="accountRule"
                    :readonly="isReadOnly"
                    :tabindex="getEditUser ? -1 : 0"
                />
            </v-col>
        </v-row>
        <v-row class="mx-1 mb-2">
            <v-col class="col col-md-6 p-0 pt-2">
                <v-select
                    v-model="ingroup"
                    outlined
                    dense
                    :items="ingroupItems"
                    label="Account Type"
                    :rules="ingroupRule"
                />
            </v-col>
        </v-row>

        <v-row class="mx-1 my-2 heading-title">
            Password
        </v-row>
        <v-divider />
        <v-row>
            <v-col class="col col-md-12 mt-5">
                <v-card
                    flat
                    color="#F5F7FA"
                >
                    <v-list-item three-line>
                        <v-list-item-content class="rule-list">
                            <div class="sub-heading-title mt-2 mb-6 instructions">
                                Password must have the following:
                            </div>
                            <v-list-item-title class="content">
                                <ul>
                                    <li
                                        v-for="rule of rules"
                                        :key="rule.description"
                                        class="rule-description"
                                        :class="[ !password ? '' : rule.test(password) ? 'rule-passed' : 'rule-failed' ]"
                                    >
                                        {{ rule.description }}
                                    </li>
                                </ul>
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-card>
            </v-col>
        </v-row>
        <v-row class="mx-1 mt-4">
            <v-col class="col col-md-6 p-0 pt-2">
                <v-text-field
                    v-model="password"
                    outlined
                    dense
                    placeholder="Password"
                    label="Password"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    :error="missingPassword"
                    :rules="ruleTests"
                    @click:append="showPassword = !showPassword"
                />
            </v-col>
            <v-col class="col col-md-6 p-0 pl-5 pt-2">
                <v-text-field
                    v-model="passwordConfirm"
                    outlined
                    dense
                    placeholder="Confirm Password"
                    label="Confirm Password"
                    :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    :error="missingConfirmPassword"
                    :error-messages="passwordConfirm.length && password !== passwordConfirm ? 'Password and confirm password must be match.' : ''"
                    @click:append="showConfirmPassword = !showConfirmPassword"
                />
            </v-col>
        </v-row>

        <v-row class="mx-1 my-2 heading-title">
            Company Details
        </v-row>
        <v-divider />
        <v-row class="mx-1 mt-4">
            <v-col class="col col-md-6 p-0">
                <v-text-field
                    v-model="company"
                    outlined
                    dense
                    placeholder="Company Name"
                    label="Company Name"
                />
            </v-col>
            <v-col class="col col-md-6 p-0 pl-5">
                <v-text-field
                    v-model="address1"
                    outlined
                    dense
                    placeholder="Address 1"
                    label="Address 1"
                />
            </v-col>
        </v-row>
        <v-row class="mx-1 mt-4">
            <v-col class="col col-md-6 p-0 ">
                <v-text-field
                    v-model="address2"
                    outlined
                    dense
                    placeholder="Address 2 (Optional)"
                    label="Address 2 (Optional)"
                />
            </v-col>
            <v-col class="col col-md-6 p-0 pl-5">
                <v-text-field
                    v-model="city"
                    outlined
                    dense
                    placeholder="City"
                    label="City"
                />
            </v-col>
        </v-row>
        <v-row class="mx-1 mt-4">
            <v-col class="col col-md-6 p-0">
                <v-select
                    v-model="state"
                    outlined
                    dense
                    label="State"
                    :items="['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY']"
                />
            </v-col>
            <v-col class="col col-md-6 p-0 pl-5">
                <v-text-field
                    v-model="zip"
                    outlined
                    dense
                    placeholder="Zip"
                    label="Zip"
                />
            </v-col>
        </v-row>

        <v-row class="mx-1 my-2 heading-title">
            Communication Details
        </v-row>
        <v-divider />
        <v-row class="mx-1 mt-4">
            <v-col class="col col-md-6 p-0">
                <v-text-field
                    v-model="phone"
                    outlined
                    dense
                    placeholder="Work Phone"
                    label="Work Phone"
                />
            </v-col>
            <v-col class="col col-md-6 p-0 pl-5">
                <v-text-field
                    v-model="cell"
                    outlined
                    dense
                    placeholder="Cell Phone"
                    label="Cell Phone"
                />
            </v-col>
        </v-row>
        <v-row class="mx-1 mt-4">
            <v-col class="col col-md-6 p-0">
                <v-text-field
                    v-model="fax"
                    outlined
                    dense
                    placeholder="Fax"
                    label="Fax"
                />
            </v-col>
        </v-row>
        
        <v-row class="mx-1 my-2 heading-title">
            Remarks
        </v-row>
        <v-divider />
        <v-row class="mx-1 mt-4">
            <v-col class="col col-md-12 p-0">
                <v-textarea
                    v-model="remarks"
                    outlined
                    name="input-7-4"
                    label="Remarks"
                />
            </v-col>
        </v-row>        
    </v-container>
</template>

<script>

import webtmsBackend from '@/services/webtmsBackend';
import { getStrengthRules } from '@/utils/password';
import { isTruthy } from '@/utils/isTruthy';

export default {
    name: 'UserInformation',
    data: () => ({
        isEditUser: false,
        ingroupItems: [],
        showConfirmPassword: false,
        missingConfirmPassword: false,
        showPassword: false,
        missingPassword: false,
        timeIntervals: [
            { name: 'Every Hour', value: '1' },
            { name: 'Every 2 Hours', value: '2' },
            { name: 'Every 3 Hours', value: '3' },
            { name: 'Every 4 Hours', value: '4' },
            { name: 'Every 5 Hours', value: '5' },
            { name: 'Every 6 Hours', value: '6' },
        ],
        name: '',
        nameRule: [
            v => !!v || 'Name is required.',
        ],
        account: '',
        accountRule: [
            v => !!v || 'Account is required.',
        ],
        ingroup: null,
        ingroupRule: [
            v => !!v || 'Account is required.',
        ],
        password: '',
        passwordConfirm: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        cell: '',
        fax: '',
        remarks: '',
        allowed: true,
        readonly: false,
        deleted: false,
        
        RECEIVE_OVERDUE_NOTIFICATION: false,
        RECEIVE_COMPANY_EOD_AUDIT: false,
        RECEIVE_COMPANY_EOD_REPORT: false,
        RECEIVE_SYSTEM_SOD: false,
        RECEIVE_SYSTEM_EOD: false,
        RECEIVE_SYSTEM_CHECK_ERRORS: false,
        RECEIVE_OVERDUE_NOTIFICATION_INTERVAL: '3',
        ALTERNATE_LOCATOR: '',
        ASSIGNED_TICKET_LIMIT: '0',
        ASSIGNMENT_ON_HOLD: false,
        FORCE_ALTERNATE_LOCATOR: false,
        EMAIL_ASSIGNED_TICKETS: false,
        VACATION1: '',
        VACATION2: '',
        VACATION3: '',
        VACATION4: '',
        VACATION5: '',
    }),
    computed: {
        getUserDetails() {
            return this.$store.getters.user;
        },
        getEditUser() {
            return this.$store.getters.editUser;
        },
        readOnly() {            
            return this.getEditUser ? true : false;
        },  
        rules() {
            const option = this.$store.getters.companyOptions.find(companyOption => {
                return companyOption.option === 'USER_PASSWORD_STRENGTH';
            });
            if(option) {
                const passwordStrength = option.value;
                return getStrengthRules(passwordStrength.toUpperCase());
            }
            return [];
        },
        isReadOnly() {            
            return this.getEditUser ? true : false;
        },  
        ruleTests() {
            let ruleTestArray = [];
            if(!this.isEditUser) {
                this.rules.forEach(rule => {
                    ruleTestArray.push(rule.test);
                });
            }
            return ruleTestArray;
        },
    },
    created() {
        let items = { SYSOP: 0, Director: 1, Administrator: 2, Dispatcher: 3, Locator: 4 };
        let permission = ['SYSOP', 'Director', 'Administrator', 'Dispatcher', 'Locator'];
        this.ingroupItems = permission.filter((item, index) => index >= items[this.getUserDetails.ingroup]);
        if(this.$route.query.id) {
            setTimeout(() => {
                this.setFormData();
            }, 100);
        } else {
            if(this.$store.getters.company) {
                if(this.getEditUser) {
                    this.setFormData();
                } else {
                    this.company = this.$store.getters.company.company;
                    this.address1 = this.$store.getters.company.address1;
                    this.address2 = this.$store.getters.company.address2;
                    this.city = this.$store.getters.company.city;
                    this.state = this.$store.getters.company.state;
                    this.zip = this.$store.getters.company.zip;
                    this.phone = this.$store.getters.company.phone ||'';
                    this.cell = this.$store.getters.company.cell||'';
                    this.fax = this.$store.getters.company.fax||'';
                }
            }
        }
    },
    methods: {
        setFormData() {
            if(this.$route.query.id) { 
                this.isEditUser = true;
            }
            if(this.getEditUser) {
                this.name = this.getEditUser.name;
                this.account = this.getEditUser.account;
                this.ingroup = this.getEditUser.ingroup;
                this.company = this.getEditUser.company;
                this.address1 = this.getEditUser.address1;
                this.address2 = this.getEditUser.address2;
                this.city = this.getEditUser.city;
                this.state = this.getEditUser.state;
                this.zip = this.getEditUser.zip;
                this.phone = this.getEditUser.phone;
                this.cell = this.getEditUser.cell;
                this.fax = this.getEditUser.fax;
                this.remarks = this.getEditUser.remarks;
                this.allowed = this.getEditUser.allowed;
                this.deleted = this.getEditUser.deleted;
                this.readonly = this.getEditUser.readonly;

                if(!this.$route.query.id) {
                    this.password = this.getEditUser.password;
                    this.passwordConfirm = this.getEditUser.passwordConfirm;
                }

                if(this.getEditUser) {
                    if(this.getEditUser.userOptions) {
                        this.getEditUser.userOptions.map(item => {
                            if(item.option === 'RECEIVE_OVERDUE_NOTIFICATION') {
                                this.RECEIVE_OVERDUE_NOTIFICATION = isTruthy(item.value);
                            }
                            if(item.option === 'RECEIVE_COMPANY_EOD_AUDIT') {
                                this.RECEIVE_COMPANY_EOD_AUDIT = isTruthy(item.value);
                            }
                            if(item.option === 'RECEIVE_COMPANY_EOD_REPORT') {
                                this.RECEIVE_COMPANY_EOD_REPORT = isTruthy(item.value);
                            }
                            if(item.option === 'RECEIVE_SYSTEM_SOD') {
                                this.RECEIVE_SYSTEM_SOD = isTruthy(item.value);
                            }
                            if(item.option === 'RECEIVE_SYSTEM_EOD') {
                                this.RECEIVE_SYSTEM_EOD = isTruthy(item.value);
                            }
                            if(item.option === 'RECEIVE_SYSTEM_CHECK_ERRORS') {
                                this.RECEIVE_SYSTEM_CHECK_ERRORS = isTruthy(item.value);
                            }
                            if(item.option === 'RECEIVE_OVERDUE_NOTIFICATION_INTERVAL') {
                                this.RECEIVE_OVERDUE_NOTIFICATION_INTERVAL = item.value;
                            }
                            if(item.option === 'ALTERNATE_LOCATOR') {
                                this.ALTERNATE_LOCATOR = item.value;
                            }
                            if(item.option === 'ASSIGNED_TICKET_LIMIT') {
                                this.ASSIGNED_TICKET_LIMIT = item.value;
                            }
                            if(item.option === 'ASSIGNMENT_ON_HOLD') {
                                this.ASSIGNMENT_ON_HOLD = isTruthy(item.value);
                            }
                            if(item.option === 'FORCE_ALTERNATE_LOCATOR') {
                                this.FORCE_ALTERNATE_LOCATOR = isTruthy(item.value);
                            }
                            if(item.option === 'EMAIL_ASSIGNED_TICKETS') {
                                this.EMAIL_ASSIGNED_TICKETS = isTruthy(item.value);
                            }
                            if(item.option === 'VACATION1') {
                                this.VACATION1 = item.value;
                            }
                            if(item.option === 'VACATION2') {
                                this.VACATION2 = item.value;
                            }
                            if(item.option === 'VACATION3') {
                                this.VACATION3 = item.value;
                            }
                            if(item.option === 'VACATION4') {
                                this.VACATION4 = item.value;
                            }
                            if(item.option === 'VACATION5') {
                                this.VACATION5 = item.value;
                            }
                        });
                    }
                }
            }
        },
        saveUserInformation() {
            let data = {
                name: this.name,
                account: this.account,
                ingroup: this.ingroup,
                password: this.password,
                passwordConfirm: this.passwordConfirm,
                company: this.company,
                address1: this.address1,
                address2: this.address2,
                city: this.city,
                state: this.state,
                zip: this.zip,
                phone: this.phone,
                cell: this.cell,
                fax: this.fax,
                remarks: this.remarks,
                allowed: this.allowed,
                deleted: this.deleted,
                readonly: this.readonly,
                RECEIVE_OVERDUE_NOTIFICATION: this.RECEIVE_OVERDUE_NOTIFICATION,
                RECEIVE_COMPANY_EOD_AUDIT: this.RECEIVE_COMPANY_EOD_AUDIT,
                RECEIVE_COMPANY_EOD_REPORT: this.RECEIVE_COMPANY_EOD_REPORT,
                RECEIVE_SYSTEM_SOD: this.RECEIVE_SYSTEM_SOD,
                RECEIVE_SYSTEM_EOD: this.RECEIVE_SYSTEM_EOD,
                RECEIVE_SYSTEM_CHECK_ERRORS: this.RECEIVE_SYSTEM_CHECK_ERRORS,
                RECEIVE_OVERDUE_NOTIFICATION_INTERVAL: this.RECEIVE_OVERDUE_NOTIFICATION_INTERVAL,
                ALTERNATE_LOCATOR: this.ALTERNATE_LOCATOR,
                ASSIGNED_TICKET_LIMIT: this.ASSIGNED_TICKET_LIMIT,
                ASSIGNMENT_ON_HOLD: this.ASSIGNMENT_ON_HOLD,
                FORCE_ALTERNATE_LOCATOR: this.FORCE_ALTERNATE_LOCATOR,
                EMAIL_ASSIGNED_TICKETS: this.EMAIL_ASSIGNED_TICKETS,
                VACATION1: this.VACATION1,
                VACATION2: this.VACATION2,
                VACATION3: this.VACATION3,
                VACATION4: this.VACATION4,
                VACATION5: this.VACATION5,
            };
            if(this.getEditUser) {
                if(this.getEditUser.userOptions) {
                    this.getEditUser.userOptions = this.getEditUser.userOptions.map(item => {
                        if(item.option === 'RECEIVE_OVERDUE_NOTIFICATION') {
                            item.value = this.RECEIVE_OVERDUE_NOTIFICATION;
                        }
                        if(item.option === 'RECEIVE_COMPANY_EOD_AUDIT') {
                            item.value = this.RECEIVE_COMPANY_EOD_AUDIT;
                        }
                        if(item.option === 'RECEIVE_COMPANY_EOD_REPORT') {
                            item.value = this.RECEIVE_COMPANY_EOD_REPORT;
                        }
                        if(item.option === 'RECEIVE_SYSTEM_SOD') {
                            item.value = this.RECEIVE_SYSTEM_SOD;
                        }
                        if(item.option === 'RECEIVE_SYSTEM_EOD') {
                            item.value = this.RECEIVE_SYSTEM_EOD;
                        }
                        if(item.option === 'RECEIVE_SYSTEM_CHECK_ERRORS') {
                            item.value = this.RECEIVE_SYSTEM_CHECK_ERRORS;
                        }
                        if(item.option === 'RECEIVE_OVERDUE_NOTIFICATION_INTERVAL') {
                            item.value = this.RECEIVE_OVERDUE_NOTIFICATION_INTERVAL;
                        }
                        if(item.option === 'ALTERNATE_LOCATOR') {
                            item.value = this.ALTERNATE_LOCATOR;
                        }
                        if(item.option === 'ASSIGNED_TICKET_LIMIT') {
                            item.value = this.ASSIGNED_TICKET_LIMIT;
                        }
                        if(item.option === 'ASSIGNMENT_ON_HOLD') {
                            item.value = this.ASSIGNMENT_ON_HOLD;
                        }
                        if(item.option === 'FORCE_ALTERNATE_LOCATOR') {
                            item.value = this.FORCE_ALTERNATE_LOCATOR;
                        }
                        if(item.option === 'EMAIL_ASSIGNED_TICKETS') {
                            item.value = this.EMAIL_ASSIGNED_TICKETS;
                        }
                        if(item.option === 'VACATION1') {
                            item.value = this.VACATION1;
                        }
                        if(item.option === 'VACATION2') {
                            item.value = this.VACATION2;
                        }
                        if(item.option === 'VACATION3') {
                            item.value = this.VACATION3;
                        }
                        if(item.option === 'VACATION4') {
                            item.value = this.VACATION4;
                        }
                        if(item.option === 'VACATION5') {
                            item.value = this.VACATION5;
                        }
                        return item;
                    });
                }

                this.$store.commit('SET_EDIT_USER', { 
                    ...this.getEditUser,
                    ...data
                });
            } else {
                data.userOptions = [
                    { option: 'RECEIVE_OVERDUE_NOTIFICATION', value: this.RECEIVE_OVERDUE_NOTIFICATION },
                    { option: 'RECEIVE_COMPANY_EOD_AUDIT', value: this.RECEIVE_COMPANY_EOD_AUDIT },
                ];
                this.$store.commit('SET_EDIT_USER', data);
            }
        },
        async editUser() {
            try {
                this.saveUserInformation();
                let result = await webtmsBackend.updateUser({uid: this.$route.query.id, ...this.getEditUser});
                if(result.status === 'OK') {
                    if(this.getEditUser.account === this.getUserDetails.account) {
                        const getUserResult = await webtmsBackend.getUserObject(this.getUserDetails.uid);
                        this.$store.commit('SET_USER', getUserResult);
                    }
                    this.$store.dispatch('setSnackbar', { text: `User account "${result.user.name}" updated successfully.` });
                    this.$store.commit('SET_EDIT_USER', null);
                    this.$router.push('users');
                }
                if(result.err) {
                    this.$store.dispatch('setSnackbar', { color:'error', text: result.err });
                }
            } catch(error) {
                this.$store.dispatch('setSnackbar', { color:'error', text: error });
            }
        }
    }
};
</script>
<style>
    .heading-title {
        font-weight: 500;
        font-size: 16px;
        color: #35383F;
    }
    .sub-heading-title {
        font-weight: 500;
        font-size: 14px;
        color: #35383F;
    }
    .content {
        font-weight: 500;
        font-size: 14px;
        color: #35383F;
    }
    .custom-checkbox { 
        display: flex;
        flex-direction: column;
    }
    .custom-checkbox .v-input--checkbox {
        margin: 0px;
        padding: 0px;
    }
    .custom-checkbox .v-input--checkbox .v-input--selection-controls__ripple {
        width: 16px;
        height: 16px;
    }
    .custom-checkbox .v-input--checkbox .v-label {
        font-weight: 400;
        font-size: 14px;
        color: #35383F;
    }
    .rule-list {
        background: #F5F7FA;
        border-radius: 4px;
        color: #35383F;
        font-style: normal;
    }
    .instructions {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.0025em;
    }
    .rule-description {
        font-weight: normal;
        font-size: 12px;
        line-height: 150%;
        letter-spacing: 0.004em;
    }
    .rule-passed {
        color: #2FA84F;
    }
    .rule-failed {
        color: #CB3D31;
    }
    .errorAlert {
        font-size: 12px;
        line-height: 20px;
        letter-spacing: 0.004em;
        color: #CB3D31;
    }
</style>